import datapoints from '../datapoints';
import disableInboxRuleService from '../services/disableInboxRuleService';
import enableInboxRuleService from '../services/enableInboxRuleService';
import { InboxRulesListOptionState } from '../store/schema/InboxRulesListOptionState';
import store from '../store/store';
import { logUsage } from 'owa-analytics';
import InboxRule from 'owa-service/lib/contract/InboxRule';
import { action, select } from 'satcheljs/lib/legacy';
import InboxRuleRetryDialog from '../components/InboxRuleRetryDialog';

export interface EnableDisableRuleOptionState {
    optionState: InboxRulesListOptionState;
}

/**
 * Enables / disables the given rule
 * @param inboxRuleId the inbox rule id
 * @param shouldBeEnabled whether the inbox rule should be enabled
 */
export default select<EnableDisableRuleOptionState>({
    optionState: () => store.inboxRulesListState,
})(
    action('enableDisableInboxRule', datapoints.enableDisableInboxRuleDatapoint)(
        function enableDisableInboxRule(
            inboxRuleId: string,
            shouldBeEnabled: boolean,
            force?: boolean,
            alwaysDeleteOutlookRulesBlob?: boolean,
            state?: EnableDisableRuleOptionState
        ) {
            let { optionState } = state;
            let inboxRule = optionState.inboxRules.get(inboxRuleId);
            setEnableDisableInboxRuleState(shouldBeEnabled, inboxRule);
            let enableDisableInboxRulePromise;

            if (inboxRule.Enabled) {
                enableDisableInboxRulePromise = enableInboxRuleService(
                    inboxRule,
                    force,
                    alwaysDeleteOutlookRulesBlob
                );
            } else {
                enableDisableInboxRulePromise = disableInboxRuleService(
                    inboxRule,
                    force,
                    alwaysDeleteOutlookRulesBlob
                );
            }

            // TODO: Proper error handling / success notification
            // Task 20143: Full error handling / success notification support
            return enableDisableInboxRulePromise
                .then(response => {
                    if (!response.WasSuccessful) {
                        setEnableDisableInboxRuleState(!shouldBeEnabled, inboxRule);

                        if (response.UserPrompt) {
                            let dialog = new InboxRuleRetryDialog(
                                response.UserPrompt,
                                isChecked =>
                                    enableDisableInboxRule(
                                        inboxRuleId,
                                        !inboxRule.Enabled,
                                        true /*force*/,
                                        isChecked /*alwaysDeleteOutlookRulesBlob*/,
                                        { optionState: store.inboxRulesListState }
                                    ),
                                () => {}
                            );
                            dialog.show();
                        }
                        logUsage(datapoints.inboxRuleActionFailDatapoint.datapoint.name, [
                            shouldBeEnabled ? 'EnableInboxRule' : 'DisableInboxRule',
                        ]);
                    }

                    return Promise.resolve();
                })
                .catch(error => {
                    logUsage(datapoints.inboxRuleActionFailDatapoint.datapoint.name, [
                        shouldBeEnabled ? 'EnableInboxRule' : 'DisableInboxRule',
                    ]);
                    return Promise.resolve();
                });
        }
    )
);

let setEnableDisableInboxRuleState = action('setEnableDisableInboxRuleState')(
    function setEnableDisableInboxRuleState(shouldBeEnabled: boolean, inboxRule: InboxRule) {
        inboxRule.Enabled = shouldBeEnabled;
    }
);
